import { useState, useEffect, useMemo } from 'react';
import { useLanguage, type Language } from '@/contexts/LanguageContext';

interface UseTranslatedContentOptions {
  sourceLang?: Language;
  skipTranslation?: boolean;
}

/**
 * Hook for handling content based on the current language
 * This version doesn't rely on external translation APIs
 */
export function useTranslatedContent<T extends string | string[] | Record<string, string>>(
  content: T,
  options: UseTranslatedContentOptions = {}
): { translatedContent: T; isLoading: boolean } {
  const { language } = useLanguage();
  const { sourceLang = 'fr', skipTranslation = false } = options;
  
  // Create a stable key for the content to avoid infinite loops
  const contentKey = useMemo(() => {
    if (typeof content === 'string') return content;
    return JSON.stringify(content);
  }, [typeof content === 'string' ? content : JSON.stringify(content)]);

  const translatedContent = useMemo<T>(() => {
    // Don't translate if explicitly requested or if target language is the same as source
    if (skipTranslation || language === sourceLang || !content) {
      return content;
    }

    try {
      if (typeof content === 'string') {
        return translateString(content as string, language) as T;
      } else if (Array.isArray(content)) {
        return (content as string[]).map(item => translateString(item, language)) as T;
      } else if (typeof content === 'object') {
        const translated = Object.entries(content as Record<string, string>).reduce(
          (acc, [key, value]) => {
            acc[key] = translateString(value, language);
            return acc;
          },
          {} as Record<string, string>
        );
        return translated as T;
      }
    } catch (error) {
      console.error('Translation error:', error);
    }
    return content;
  }, [contentKey, language, sourceLang, skipTranslation]);

  return { translatedContent, isLoading: false };
}

// Simple translation function based on predefined translations
function translateString(text: string, targetLang: Language): string {
  // Dictionary of common translations
  const translations: Record<string, Record<Language, string>> = {
    // About section
    "Votre Oasis de Détente au Grau du Roi": {
      fr: "Votre Oasis de Détente au Grau du Roi",
      en: "Your Relaxation Oasis in Le Grau du Roi",
      es: "Su Oasis de Relajación en Le Grau du Roi",
      it: "La Tua Oasi di Relax a Le Grau du Roi",
      de: "Ihre Entspannungsoase in Le Grau du Roi",
      nl: "Uw Ontspanningsoase in Le Grau du Roi"
    },
    "Nichée dans un quartier paisible du Grau du Roi, la Villa Douce Vague vous offre un cadre idyllique pour vos vacances en bord de mer. À seulement quelques minutes à pied des plages de sable fin et du célèbre port de pêche, notre villa allie confort moderne et charme méditerranéen.": {
      fr: "Nichée dans un quartier paisible du Grau du Roi, la Villa Douce Vague vous offre un cadre idyllique pour vos vacances en bord de mer. À seulement quelques minutes à pied des plages de sable fin et du célèbre port de pêche, notre villa allie confort moderne et charme méditerranéen.",
      en: "Nestled in a peaceful neighborhood of Le Grau du Roi, Villa Douce Vague offers you an idyllic setting for your seaside vacation. Just a few minutes walk from fine sandy beaches and the famous fishing port, our villa combines modern comfort with Mediterranean charm.",
      es: "Ubicada en un tranquilo barrio de Le Grau du Roi, Villa Douce Vague le ofrece un entorno idílico para sus vacaciones junto al mar. A solo unos minutos a pie de las playas de arena fina y del famoso puerto pesquero, nuestra villa combina comodidad moderna con encanto mediterráneo.",
      it: "Situata in un tranquillo quartiere di Le Grau du Roi, Villa Douce Vague offre un ambiente idilliaco per le tue vacanze al mare. A pochi minuti a piedi dalle spiagge di sabbia fine e dal famoso porto di pesca, la nostra villa combina comfort moderno e fascino mediterraneo.",
      de: "In einem ruhigen Viertel von Le Grau du Roi gelegen, bietet Ihnen die Villa Douce Vague eine idyllische Umgebung für Ihren Urlaub am Meer. Nur wenige Gehminuten von feinen Sandstränden und dem berühmten Fischerhafen entfernt, verbindet unsere Villa modernen Komfort mit mediterranem Charme.",
      nl: "Genesteld in een rustige wijk van Le Grau du Roi, biedt Villa Douce Vague u een idyllische omgeving voor uw vakantie aan zee. Op slechts enkele minuten lopen van fijne zandstranden en de beroemde vissershaven, combineert onze villa modern comfort met mediterrane charme."
    },
    "Avec ses 160m² d'espace de vie, sa piscine privée chauffée et son jardin luxuriant, la villa Douce Vague peut accueillir jusqu'à 8 personnes dans un cadre élégant et raffiné. Profitez de moments de détente sur la terrasse ensoleillée ou partez à la découverte des trésors de la Camargue.": {
      fr: "Avec ses 160m² d'espace de vie, sa piscine privée chauffée et son jardin luxuriant, la villa Douce Vague peut accueillir jusqu'à 8 personnes dans un cadre élégant et raffiné. Profitez de moments de détente sur la terrasse ensoleillée ou partez à la découverte des trésors de la Camargue.",
      en: "With its 160m² of living space, private heated pool and lush garden, Villa Douce Vague can accommodate up to 8 people in an elegant and refined setting. Enjoy moments of relaxation on the sunny terrace or go discover the treasures of the Camargue.",
      es: "Con sus 160m² de espacio habitable, piscina privada climatizada y exuberante jardín, Villa Douce Vague puede albergar hasta 8 personas en un entorno elegante y refinado. Disfrute de momentos de relajación en la soleada terraza o descubra los tesoros de la Camarga.",
      it: "Con i suoi 160m² di spazio abitativo, piscina privata riscaldata e giardino rigoglioso, Villa Douce Vague può ospitare fino a 8 persone in un ambiente elegante e raffinato. Goditi momenti di relax sulla terrazza soleggiata o vai alla scoperta dei tesori della Camargue.",
      de: "Mit 160 m² Wohnfläche, privatem beheiztem Pool und üppigem Garten bietet die Villa Douce Vague Platz für bis zu 8 Personen in einem eleganten und raffinierten Ambiente. Genießen Sie entspannte Momente auf der sonnigen Terrasse oder entdecken Sie die Schätze der Camargue.",
      nl: "Met 160m² woonoppervlak, privé verwarmd zwembad en weelderige tuin, kan Villa Douce Vague tot 8 personen accommoderen in een elegante en verfijnde omgeving. Geniet van ontspannende momenten op het zonnige terras of ga op ontdekking naar de schatten van de Camargue."
    },
    "Découvrir les équipements": {
      fr: "Découvrir les équipements",
      en: "Discover the amenities",
      es: "Descubrir los servicios",
      it: "Scopri i servizi",
      de: "Entdecken Sie die Ausstattung",
      nl: "Ontdek de voorzieningen"
    },
    // Booking section
    "Planifiez Votre Séjour de Rêve": {
      fr: "Planifiez Votre Séjour de Rêve",
      en: "Plan Your Dream Stay",
      es: "Planifique su Estancia de Ensueño",
      it: "Pianifica il Tuo Soggiorno da Sogno",
      de: "Planen Sie Ihren Traumaufenthalt",
      nl: "Plan Uw Droomverblijf"
    },
    "Réservez dès maintenant votre séjour à la villa Douce Vague et profitez d'une expérience inoubliable au cœur du Grau du Roi.": {
      fr: "Réservez dès maintenant votre séjour à la villa Douce Vague et profitez d'une expérience inoubliable au cœur du Grau du Roi.",
      en: "Book your stay at Villa Douce Vague now and enjoy an unforgettable experience in the heart of Le Grau du Roi.",
      es: "Reserve ahora su estancia en Villa Douce Vague y disfrute de una experiencia inolvidable en el corazón de Le Grau du Roi.",
      it: "Prenota ora il tuo soggiorno a Villa Douce Vague e goditi un'esperienza indimenticabile nel cuore di Le Grau du Roi.",
      de: "Buchen Sie jetzt Ihren Aufenthalt in der Villa Douce Vague und genießen Sie ein unvergessliches Erlebnis im Herzen von Le Grau du Roi.",
      nl: "Boek nu uw verblijf in Villa Douce Vague en geniet van een onvergetelijke ervaring in het hart van Le Grau du Roi."
    },
    "Pourquoi choisir la Villa Douce Vague ?": {
      fr: "Pourquoi choisir la Villa Douce Vague ?",
      en: "Why choose Villa Douce Vague?",
      es: "¿Por qué elegir Villa Douce Vague?",
      it: "Perché scegliere Villa Douce Vague?",
      de: "Warum Villa Douce Vague wählen?",
      nl: "Waarom kiezen voor Villa Douce Vague?"
    },
    "Villa spacieuse et moderne avec piscine privée chauffée": {
      fr: "Villa spacieuse et moderne avec piscine privée chauffée",
      en: "Spacious and modern villa with heated private pool",
      es: "Villa espaciosa y moderna con piscina privada climatizada",
      it: "Villa spaziosa e moderna con piscina privata riscaldata",
      de: "Geräumige und moderne Villa mit beheiztem Privatpool",
      nl: "Ruime en moderne villa met verwarmd privé zwembad"
    },
    "Emplacement idéal à quelques pas des plages et des commerces": {
      fr: "Emplacement idéal à quelques pas des plages et des commerces",
      en: "Ideal location just steps away from beaches and shops",
      es: "Ubicación ideal a pocos pasos de playas y tiendas",
      it: "Posizione ideale a pochi passi dalle spiagge e dai negozi",
      de: "Ideale Lage, nur wenige Schritte von Stränden und Geschäften entfernt",
      nl: "Ideale locatie op loopafstand van stranden en winkels"
    },
    "Équipements premium pour un confort optimal": {
      fr: "Équipements premium pour un confort optimal",
      en: "Premium amenities for optimal comfort",
      es: "Servicios premium para un confort óptimo",
      it: "Servizi premium per un comfort ottimale",
      de: "Premium-Ausstattung für optimalen Komfort",
      nl: "Premium voorzieningen voor optimaal comfort"
    },
    "Parfait pour les familles et les groupes d'amis": {
      fr: "Parfait pour les familles et les groupes d'amis",
      en: "Perfect for families and groups of friends",
      es: "Perfecto para familias y grupos de amigos",
      it: "Perfetto per famiglie e gruppi di amici",
      de: "Perfekt für Familien und Freundesgruppen",
      nl: "Perfect voor families en groepen vrienden"
    },
    "À proximité de nombreuses activités et sites touristiques": {
      fr: "À proximité de nombreuses activités et sites touristiques",
      en: "Close to many activities and tourist attractions",
      es: "Cerca de numerosas actividades y lugares turísticos",
      it: "Vicino a molte attività e attrazioni turistiche",
      de: "In der Nähe vieler Aktivitäten und Sehenswürdigkeiten",
      nl: "Dicht bij vele activiteiten en toeristische attracties"
    },
    "Tarifs & Disponibilités": {
      fr: "Tarifs & Disponibilités",
      en: "Rates & Availability",
      es: "Tarifas y Disponibilidad",
      it: "Tariffe e Disponibilità",
      de: "Preise & Verfügbarkeit",
      nl: "Tarieven & Beschikbaarheid"
    },
    "Haute saison (juillet-août) : à partir de": {
      fr: "Haute saison (juillet-août) : à partir de",
      en: "High season (July-August): from",
      es: "Temporada alta (julio-agosto): desde",
      it: "Alta stagione (luglio-agosto): da",
      de: "Hochsaison (Juli-August): ab",
      nl: "Hoogseizoen (juli-augustus): vanaf"
    },
    "Moyenne saison (mai-juin, septembre) : à partir de": {
      fr: "Moyenne saison (mai-juin, septembre) : à partir de",
      en: "Mid-season (May-June, September): from",
      es: "Media temporada (mayo-junio, septiembre): desde",
      it: "Media stagione (maggio-giugno, settembre): da",
      de: "Mittelsaison (Mai-Juni, September): ab",
      nl: "Middenseizoen (mei-juni, september): vanaf"
    },
    "Basse saison : à partir de": {
      fr: "Basse saison : à partir de",
      en: "Low season: from",
      es: "Temporada baja: desde",
      it: "Bassa stagione: da",
      de: "Nebensaison: ab",
      nl: "Laagseizoen: vanaf"
    },
    "Séjour minimum de 7 nuits en haute saison": {
      fr: "Séjour minimum de 7 nuits en haute saison",
      en: "Minimum stay of 7 nights in high season",
      es: "Estancia mínima de 7 noches en temporada alta",
      it: "Soggiorno minimo di 7 notti in alta stagione",
      de: "Mindestaufenthalt von 7 Nächten in der Hochsaison",
      nl: "Minimumverblijf van 7 nachten in het hoogseizoen"
    },
    "/nuit": {
      fr: "/nuit",
      en: "/night",
      es: "/noche",
      it: "/notte",
      de: "/Nacht",
      nl: "/nacht"
    },
    // Hero Section
    "Bienvenue à la Villa Douce Vague": {
      fr: "Bienvenue à la Villa Douce Vague",
      en: "Welcome to Villa Douce Vague",
      es: "Bienvenido a Villa Douce Vague",
      it: "Benvenuti a Villa Douce Vague",
      de: "Willkommen in der Villa Douce Vague",
      nl: "Welkom bij Villa Douce Vague"
    },
    "Votre location de vacances exclusive au Grau du Roi": {
      fr: "Votre location de vacances exclusive au Grau du Roi",
      en: "Your exclusive vacation rental in Le Grau du Roi",
      es: "Su alquiler vacacional exclusivo en Le Grau du Roi",
      it: "Il tuo affitto vacanze esclusivo a Le Grau du Roi",
      de: "Ihre exklusive Ferienvermietung in Le Grau du Roi",
      nl: "Uw exclusieve vakantieverhuur in Le Grau du Roi"
    },
    "Réserver maintenant": {
      fr: "Réserver maintenant",
      en: "Book now",
      es: "Reservar ahora",
      it: "Prenota ora",
      de: "Jetzt buchen",
      nl: "Nu boeken"
    },
    // Other pages
    "Réserver la Villa Douce Vague": {
      fr: "Réserver la Villa Douce Vague",
      en: "Book Villa Douce Vague",
      es: "Reservar Villa Douce Vague",
      it: "Prenota Villa Douce Vague",
      de: "Buchen Sie Villa Douce Vague",
      nl: "Boek Villa Douce Vague"
    },
    "Galerie": {
      fr: "Galerie",
      en: "Gallery",
      es: "Galería",
      it: "Galleria",
      de: "Galerie",
      nl: "Galerij"
    },
    "Contact": {
      fr: "Contact",
      en: "Contact",
      es: "Contacto",
      it: "Contatto",
      de: "Kontakt",
      nl: "Contact"
    },
    "Localisation": {
      fr: "Localisation",
      en: "Location",
      es: "Ubicación",
      it: "Posizione",
      de: "Lage",
      nl: "Locatie"
    },
    "Nos contacts": {
      fr: "Nos contacts",
      en: "Our contacts",
      es: "Nuestros contactos",
      it: "I nostri contatti",
      de: "Unsere Kontakte",
      nl: "Onze contacten"
    },
    // Villa Features
    "Les Prestations de la Villa": {
      fr: "Les Prestations de la Villa",
      en: "Villa Amenities",
      es: "Servicios de la Villa",
      it: "Servizi della Villa",
      de: "Villa-Ausstattung",
      nl: "Villa Voorzieningen"
    },
    "Villa Spacieuse": {
      fr: "Villa Spacieuse",
      en: "Spacious Villa",
      es: "Villa Espaciosa",
      it: "Villa Spaziosa",
      de: "Geräumige Villa",
      nl: "Ruime Villa"
    },
    "160m² de surface habitable pour un confort optimal": {
      fr: "160m² de surface habitable pour un confort optimal",
      en: "160m² of living space for optimal comfort",
      es: "160m² de espacio habitable para un confort óptimo",
      it: "160m² di spazio abitativo per un comfort ottimale",
      de: "160m² Wohnfläche für optimalen Komfort",
      nl: "160m² woonoppervlak voor optimaal comfort"
    },
    "Capacité": {
      fr: "Capacité",
      en: "Capacity",
      es: "Capacidad",
      it: "Capacità",
      de: "Kapazität",
      nl: "Capaciteit"
    },
    "Accueille jusqu'à 8 personnes confortablement": {
      fr: "Accueille jusqu'à 8 personnes confortablement",
      en: "Accommodates up to 8 people comfortably",
      es: "Aloja hasta 8 personas cómodamente",
      it: "Ospita comodamente fino a 8 persone",
      de: "Bietet Platz für bis zu 8 Personen",
      nl: "Biedt comfortabel plaats aan 8 personen"
    },
    "Piscine Privée": {
      fr: "Piscine Privée",
      en: "Private Pool",
      es: "Piscina Privada",
      it: "Piscina Privata",
      de: "Privater Pool",
      nl: "Privé Zwembad"
    },
    "Profitez d'une piscine chauffée sur une terrasse ensoleillée": {
      fr: "Profitez d'une piscine chauffée sur une terrasse ensoleillée",
      en: "Enjoy a heated pool on a sunny terrace",
      es: "Disfrute de una piscina climatizada en una terraza soleada",
      it: "Goditi una piscina riscaldata su una terrazza soleggiata",
      de: "Genießen Sie einen beheizten Pool auf einer sonnigen Terrasse",
      nl: "Geniet van een verwarmd zwembad op een zonnig terras"
    },
    "Proche de la Plage": {
      fr: "Proche de la Plage",
      en: "Near the Beach",
      es: "Cerca de la Playa",
      it: "Vicino alla Spiaggia",
      de: "Strandnähe",
      nl: "Dicht bij het Strand"
    },
    "À 5 minutes à pied des plages de sable fin": {
      fr: "À 5 minutes à pied des plages de sable fin",
      en: "5 minutes walk from fine sandy beaches",
      es: "A 5 minutos a pie de playas de arena fina",
      it: "A 5 minuti a piedi dalle spiagge di sabbia fine",
      de: "5 Gehminuten von feinen Sandstränden",
      nl: "5 minuten lopen van fijne zandstranden"
    },
    "Wifi Haut Débit": {
      fr: "Wifi Haut Débit",
      en: "High-Speed WiFi",
      es: "WiFi de Alta Velocidad",
      it: "WiFi ad Alta Velocità",
      de: "Highspeed-WLAN",
      nl: "Snelle WiFi"
    },
    "Restez connecté avec une connexion internet fibre": {
      fr: "Restez connecté avec une connexion internet fibre",
      en: "Stay connected with fiber internet",
      es: "Manténgase conectado con internet de fibra",
      it: "Resta connesso con internet in fibra",
      de: "Bleiben Sie mit Glasfaser-Internet verbunden",
      nl: "Blijf verbonden met glasvezelinternet"
    },
    "Divertissement": {
      fr: "Divertissement",
      en: "Entertainment",
      es: "Entretenimiento",
      it: "Intrattenimento",
      de: "Unterhaltung",
      nl: "Entertainment"
    },
    "TV écran plat, système audio et jeux de société": {
      fr: "TV écran plat, système audio et jeux de société",
      en: "Flat screen TV, audio system and board games",
      es: "TV de pantalla plana, sistema de audio y juegos de mesa",
      it: "TV a schermo piatto, sistema audio e giochi da tavolo",
      de: "Flachbildfernseher, Audiosystem und Brettspiele",
      nl: "Flatscreen TV, audiosysteem en bordspellen"
    },
    "Climatisation": {
      fr: "Climatisation",
      en: "Air Conditioning",
      es: "Aire Acondicionado",
      it: "Aria Condizionata",
      de: "Klimaanlage",
      nl: "Airconditioning"
    },
    "Climatisation dans toutes les chambres pour votre confort": {
      fr: "Climatisation dans toutes les chambres pour votre confort",
      en: "Air conditioning in all rooms for your comfort",
      es: "Aire acondicionado en todas las habitaciones para su comodidad",
      it: "Aria condizionata in tutte le camere per il vostro comfort",
      de: "Klimaanlage in allen Zimmern für Ihren Komfort",
      nl: "Airconditioning in alle kamers voor uw comfort"
    },
    "Stationnement": {
      fr: "Stationnement",
      en: "Parking",
      es: "Aparcamiento",
      it: "Parcheggio",
      de: "Parkplatz",
      nl: "Parkeren"
    },
    "Places de parking disponibles devant la villa": {
      fr: "Places de parking disponibles devant la villa",
      en: "Parking spaces available in front of the villa",
      es: "Plazas de aparcamiento disponibles frente a la villa",
      it: "Posti auto disponibili davanti alla villa",
      de: "Parkplätze vor der Villa verfügbar",
      nl: "Parkeerplaatsen beschikbaar voor de villa"
    },
    // Newsletter Popup
    "Restez informé !": {
      fr: "Restez informé !",
      en: "Stay informed!",
      es: "¡Manténgase informado!",
      it: "Resta informato!",
      de: "Bleiben Sie informiert!",
      nl: "Blijf op de hoogte!"
    },
    "Inscrivez-vous à notre newsletter pour ne rien manquer de nos actualités et offres spéciales.": {
      fr: "Inscrivez-vous à notre newsletter pour ne rien manquer de nos actualités et offres spéciales.",
      en: "Subscribe to our newsletter to never miss our news and special offers.",
      es: "Suscríbase a nuestro boletín para no perderse nuestras noticias y ofertas especiales.",
      it: "Iscriviti alla nostra newsletter per non perdere le nostre novità e offerte speciali.",
      de: "Abonnieren Sie unseren Newsletter, um keine Neuigkeiten und Sonderangebote zu verpassen.",
      nl: "Schrijf u in voor onze nieuwsbrief om ons nieuws en speciale aanbiedingen niet te missen."
    },
    // Footer
    "Une villa d'exception au Grau du Roi, offrant confort et élégance pour des vacances inoubliables sur la côte méditerranéenne.": {
      fr: "Une villa d'exception au Grau du Roi, offrant confort et élégance pour des vacances inoubliables sur la côte méditerranéenne.",
      en: "An exceptional villa in Le Grau du Roi, offering comfort and elegance for unforgettable holidays on the Mediterranean coast.",
      es: "Una villa excepcional en Le Grau du Roi, que ofrece comodidad y elegancia para unas vacaciones inolvidables en la costa mediterránea.",
      it: "Una villa eccezionale a Le Grau du Roi, che offre comfort ed eleganza per vacanze indimenticabili sulla costa mediterranea.",
      de: "Eine außergewöhnliche Villa in Le Grau du Roi, die Komfort und Eleganz für unvergessliche Ferien an der Mittelmeerküste bietet.",
      nl: "Een uitzonderlijke villa in Le Grau du Roi, met comfort en elegantie voor onvergetelijke vakanties aan de Middellandse Zeekust."
    },
    "Liens Rapides": {
      fr: "Liens Rapides",
      en: "Quick Links",
      es: "Enlaces Rápidos",
      it: "Link Rapidi",
      de: "Schnelllinks",
      nl: "Snelle Links"
    },
    "Accueil": {
      fr: "Accueil",
      en: "Home",
      es: "Inicio",
      it: "Home",
      de: "Startseite",
      nl: "Home"
    },
    "Équipements": {
      fr: "Équipements",
      en: "Equipment",
      es: "Equipamiento",
      it: "Attrezzature",
      de: "Ausstattung",
      nl: "Uitrusting"
    },
    "Les environs": {
      fr: "Les environs",
      en: "Surroundings",
      es: "Alrededores",
      it: "Dintorni",
      de: "Umgebung",
      nl: "Omgeving"
    },
    "Réserver": {
      fr: "Réserver",
      en: "Book",
      es: "Reservar",
      it: "Prenota",
      de: "Buchen",
      nl: "Boeken"
    },
    "Newsletter": {
      fr: "Newsletter",
      en: "Newsletter",
      es: "Boletín",
      it: "Newsletter",
      de: "Newsletter",
      nl: "Nieuwsbrief"
    },
    "Inscrivez-vous pour recevoir nos offres spéciales et les dernières actualités.": {
      fr: "Inscrivez-vous pour recevoir nos offres spéciales et les dernières actualités.",
      en: "Subscribe to receive our special offers and latest news.",
      es: "Suscríbase para recibir nuestras ofertas especiales y las últimas noticias.",
      it: "Iscriviti per ricevere le nostre offerte speciali e le ultime novità.",
      de: "Abonnieren Sie, um unsere Sonderangebote und neuesten Nachrichten zu erhalten.",
      nl: "Schrijf u in om onze speciale aanbiedingen en laatste nieuws te ontvangen."
    },
    "Tous droits réservés.": {
      fr: "Tous droits réservés.",
      en: "All rights reserved.",
      es: "Todos los derechos reservados.",
      it: "Tutti i diritti riservati.",
      de: "Alle Rechte vorbehalten.",
      nl: "Alle rechten voorbehouden."
    },
    "Mentions légales": {
      fr: "Mentions légales",
      en: "Legal Notice",
      es: "Aviso Legal",
      it: "Note Legali",
      de: "Impressum",
      nl: "Juridische Kennisgeving"
    },
    "Politique de confidentialité": {
      fr: "Politique de confidentialité",
      en: "Privacy Policy",
      es: "Política de Privacidad",
      it: "Politica sulla Privacy",
      de: "Datenschutzrichtlinie",
      nl: "Privacybeleid"
    },
    // Equipment page
    "Équipements de la Villa": {
      fr: "Équipements de la Villa",
      en: "Villa Equipment",
      es: "Equipamiento de la Villa",
      it: "Attrezzature della Villa",
      de: "Villa-Ausstattung",
      nl: "Villa Uitrusting"
    },
    "Découvrez tous les équipements luxueux que la Villa Douce Vague met à votre disposition pour un séjour inoubliable au Grau du Roi.": {
      fr: "Découvrez tous les équipements luxueux que la Villa Douce Vague met à votre disposition pour un séjour inoubliable au Grau du Roi.",
      en: "Discover all the luxurious amenities that Villa Douce Vague offers for an unforgettable stay in Le Grau du Roi.",
      es: "Descubra todos los lujosos servicios que Villa Douce Vague pone a su disposición para una estancia inolvidable en Le Grau du Roi.",
      it: "Scopri tutti i servizi di lusso che Villa Douce Vague mette a tua disposizione per un soggiorno indimenticabile a Le Grau du Roi.",
      de: "Entdecken Sie alle luxuriösen Annehmlichkeiten, die Villa Douce Vague für einen unvergesslichen Aufenthalt in Le Grau du Roi bietet.",
      nl: "Ontdek alle luxe voorzieningen die Villa Douce Vague biedt voor een onvergetelijk verblijf in Le Grau du Roi."
    },
    "Intérieur": {
      fr: "Intérieur",
      en: "Interior",
      es: "Interior",
      it: "Interni",
      de: "Innenbereich",
      nl: "Interieur"
    },
    "Chambres": {
      fr: "Chambres",
      en: "Bedrooms",
      es: "Dormitorios",
      it: "Camere",
      de: "Schlafzimmer",
      nl: "Slaapkamers"
    },
    "Salles de bain": {
      fr: "Salles de bain",
      en: "Bathrooms",
      es: "Baños",
      it: "Bagni",
      de: "Badezimmer",
      nl: "Badkamers"
    },
    "Extérieur": {
      fr: "Extérieur",
      en: "Outdoor",
      es: "Exterior",
      it: "Esterno",
      de: "Außenbereich",
      nl: "Buiten"
    },
    "Espace de vie": {
      fr: "Espace de vie",
      en: "Living Space",
      es: "Espacio de Vida",
      it: "Spazio Abitativo",
      de: "Wohnbereich",
      nl: "Woonruimte"
    },
    "Chambres confortables": {
      fr: "Chambres confortables",
      en: "Comfortable Bedrooms",
      es: "Dormitorios Cómodos",
      it: "Camere Confortevoli",
      de: "Komfortable Schlafzimmer",
      nl: "Comfortabele Slaapkamers"
    },
    "Salles de bain modernes": {
      fr: "Salles de bain modernes",
      en: "Modern Bathrooms",
      es: "Baños Modernos",
      it: "Bagni Moderni",
      de: "Moderne Badezimmer",
      nl: "Moderne Badkamers"
    },
    "Extérieur & Jardin": {
      fr: "Extérieur & Jardin",
      en: "Outdoor & Garden",
      es: "Exterior y Jardín",
      it: "Esterno e Giardino",
      de: "Außenbereich & Garten",
      nl: "Buiten & Tuin"
    },
    "Services additionnels disponibles sur demande": {
      fr: "Services additionnels disponibles sur demande",
      en: "Additional services available on request",
      es: "Servicios adicionales disponibles bajo petición",
      it: "Servizi aggiuntivi disponibili su richiesta",
      de: "Zusätzliche Dienstleistungen auf Anfrage verfügbar",
      nl: "Extra diensten beschikbaar op aanvraag"
    },
    "Massages & Bien-être": {
      fr: "Massages & Bien-être",
      en: "Massages & Wellness",
      es: "Masajes y Bienestar",
      it: "Massaggi e Benessere",
      de: "Massagen & Wellness",
      nl: "Massages & Wellness"
    },
    "Détendez-vous avec nos services de massage et soins bien-être à domicile.": {
      fr: "Détendez-vous avec nos services de massage et soins bien-être à domicile.",
      en: "Relax with our in-home massage and wellness services.",
      es: "Relájese con nuestros servicios de masajes y bienestar a domicilio.",
      it: "Rilassati con i nostri servizi di massaggi e benessere a domicilio.",
      de: "Entspannen Sie sich mit unseren Massage- und Wellness-Services zu Hause.",
      nl: "Ontspan met onze massage- en wellnessdiensten aan huis."
    },
    // Equipment features
    "Espace de vie spacieux et lumineux": {
      fr: "Espace de vie spacieux et lumineux",
      en: "Spacious and bright living space",
      es: "Espacio de vida amplio y luminoso",
      it: "Spazio abitativo spazioso e luminoso",
      de: "Geräumiger und heller Wohnbereich",
      nl: "Ruime en lichte woonruimte"
    },
    "Cuisine ouverte entièrement équipée": {
      fr: "Cuisine ouverte entièrement équipée",
      en: "Fully equipped open kitchen",
      es: "Cocina abierta totalmente equipada",
      it: "Cucina aperta completamente attrezzata",
      de: "Voll ausgestattete offene Küche",
      nl: "Volledig uitgeruste open keuken"
    },
    "Salle à manger pouvant accueillir 8 personnes": {
      fr: "Salle à manger pouvant accueillir 8 personnes",
      en: "Dining room seating 8 people",
      es: "Comedor con capacidad para 8 personas",
      it: "Sala da pranzo per 8 persone",
      de: "Esszimmer für 8 Personen",
      nl: "Eetkamer voor 8 personen"
    },
    "Salon confortable avec smart TV 55 pouces": {
      fr: "Salon confortable avec smart TV 55 pouces",
      en: "Comfortable living room with 55-inch smart TV",
      es: "Sala de estar cómoda con smart TV de 55 pulgadas",
      it: "Soggiorno confortevole con smart TV da 55 pollici",
      de: "Gemütliches Wohnzimmer mit 55-Zoll Smart-TV",
      nl: "Comfortabele woonkamer met 55-inch smart TV"
    },
    "Connexion Wi-Fi haut débit gratuite": {
      fr: "Connexion Wi-Fi haut débit gratuite",
      en: "Free high-speed Wi-Fi connection",
      es: "Conexión Wi-Fi de alta velocidad gratuita",
      it: "Connessione Wi-Fi ad alta velocità gratuita",
      de: "Kostenlose Highspeed-WLAN-Verbindung",
      nl: "Gratis snelle Wi-Fi-verbinding"
    },
    "4 chambres pouvant accueillir jusqu'à 8 personnes": {
      fr: "4 chambres pouvant accueillir jusqu'à 8 personnes",
      en: "4 bedrooms accommodating up to 8 people",
      es: "4 dormitorios con capacidad para 8 personas",
      it: "4 camere che possono ospitare fino a 8 persone",
      de: "4 Schlafzimmer für bis zu 8 Personen",
      nl: "4 slaapkamers voor maximaal 8 personen"
    },
    "Chambre principale avec lit king-size et salle de bain privative": {
      fr: "Chambre principale avec lit king-size et salle de bain privative",
      en: "Master bedroom with king-size bed and private bathroom",
      es: "Dormitorio principal con cama king-size y baño privado",
      it: "Camera principale con letto king-size e bagno privato",
      de: "Hauptschlafzimmer mit King-Size-Bett und eigenem Bad",
      nl: "Hoofdslaapkamer met kingsize bed en eigen badkamer"
    },
    "Seconde chambre avec lit queen-size": {
      fr: "Seconde chambre avec lit queen-size",
      en: "Second bedroom with queen-size bed",
      es: "Segunda habitación con cama queen-size",
      it: "Seconda camera con letto queen-size",
      de: "Zweites Schlafzimmer mit Queen-Size-Bett",
      nl: "Tweede slaapkamer met queensize bed"
    },
    "Troisième chambre avec lit queen-size": {
      fr: "Troisième chambre avec lit queen-size",
      en: "Third bedroom with queen-size bed",
      es: "Tercera habitación con cama queen-size",
      it: "Terza camera con letto queen-size",
      de: "Drittes Schlafzimmer mit Queen-Size-Bett",
      nl: "Derde slaapkamer met queensize bed"
    },
    "Quatrième chambre avec deux lits simples": {
      fr: "Quatrième chambre avec deux lits simples",
      en: "Fourth bedroom with two single beds",
      es: "Cuarta habitación con dos camas individuales",
      it: "Quarta camera con due letti singoli",
      de: "Viertes Schlafzimmer mit zwei Einzelbetten",
      nl: "Vierde slaapkamer met twee eenpersoonsbedden"
    },
    "Literie de qualité hôtelière": {
      fr: "Literie de qualité hôtelière",
      en: "Hotel-quality bedding",
      es: "Ropa de cama de calidad hotelera",
      it: "Biancheria da letto di qualità alberghiera",
      de: "Bettwäsche in Hotelqualität",
      nl: "Beddengoed van hotelkwaliteit"
    },
    "Placards spacieux dans chaque chambre": {
      fr: "Placards spacieux dans chaque chambre",
      en: "Spacious closets in each bedroom",
      es: "Armarios espaciosos en cada habitación",
      it: "Armadi spaziosi in ogni camera",
      de: "Geräumige Schränke in jedem Schlafzimmer",
      nl: "Ruime kasten in elke slaapkamer"
    },
    "Climatisation individuelle dans chaque chambre": {
      fr: "Climatisation individuelle dans chaque chambre",
      en: "Individual air conditioning in each bedroom",
      es: "Aire acondicionado individual en cada habitación",
      it: "Aria condizionata individuale in ogni camera",
      de: "Individuelle Klimaanlage in jedem Schlafzimmer",
      nl: "Individuele airconditioning in elke slaapkamer"
    },
    "3 salles de bain élégantes": {
      fr: "3 salles de bain élégantes",
      en: "3 elegant bathrooms",
      es: "3 baños elegantes",
      it: "3 bagni eleganti",
      de: "3 elegante Badezimmer",
      nl: "3 elegante badkamers"
    },
    "2 salles de bain avec douche": {
      fr: "2 salles de bain avec douche",
      en: "2 bathrooms with shower",
      es: "2 baños con ducha",
      it: "2 bagni con doccia",
      de: "2 Badezimmer mit Dusche",
      nl: "2 badkamers met douche"
    },
    "1 salle de bain avec baignoire": {
      fr: "1 salle de bain avec baignoire",
      en: "1 bathroom with bathtub",
      es: "1 baño con bañera",
      it: "1 bagno con vasca da bagno",
      de: "1 Badezimmer mit Badewanne",
      nl: "1 badkamer met bad"
    },
    "Sèche-cheveux dans chaque salle de bain": {
      fr: "Sèche-cheveux dans chaque salle de bain",
      en: "Hair dryer in each bathroom",
      es: "Secador de pelo en cada baño",
      it: "Asciugacapelli in ogni bagno",
      de: "Haartrockner in jedem Badezimmer",
      nl: "Haardroger in elke badkamer"
    },
    "Serviettes de qualité fournies": {
      fr: "Serviettes de qualité fournies",
      en: "Quality towels provided",
      es: "Toallas de calidad incluidas",
      it: "Asciugamani di qualità forniti",
      de: "Hochwertige Handtücher werden gestellt",
      nl: "Kwaliteitshanddoeken aanwezig"
    },
    "Produits d'accueil haut de gamme": {
      fr: "Produits d'accueil haut de gamme",
      en: "High-end welcome products",
      es: "Productos de bienvenida de alta gama",
      it: "Prodotti di benvenuto di alta gamma",
      de: "Hochwertige Pflegeprodukte",
      nl: "Hoogwaardige welkomstproducten"
    },
    "Piscine privée chauffée (8m x 4m)": {
      fr: "Piscine privée chauffée (8m x 4m)",
      en: "Heated private pool (8m x 4m)",
      es: "Piscina privada climatizada (8m x 4m)",
      it: "Piscina privata riscaldata (8m x 4m)",
      de: "Beheizter Privatpool (8m x 4m)",
      nl: "Verwarmd privé zwembad (8m x 4m)"
    },
    "Grande terrasse ensoleillée avec mobilier d'extérieur": {
      fr: "Grande terrasse ensoleillée avec mobilier d'extérieur",
      en: "Large sunny terrace with outdoor furniture",
      es: "Amplia terraza soleada con mobiliario de exterior",
      it: "Ampia terrazza soleggiata con mobili da esterno",
      de: "Große sonnige Terrasse mit Gartenmöbeln",
      nl: "Groot zonnig terras met tuinmeubelen"
    },
    "Espace repas extérieur couvert": {
      fr: "Espace repas extérieur couvert",
      en: "Covered outdoor dining area",
      es: "Zona de comedor exterior cubierta",
      it: "Area pranzo esterna coperta",
      de: "Überdachter Essbereich im Freien",
      nl: "Overdekte buiteneetruimte"
    },
    "Plancha à gaz": {
      fr: "Plancha à gaz",
      en: "Gas plancha grill",
      es: "Plancha de gas",
      it: "Piastra a gas",
      de: "Gas-Plancha",
      nl: "Gas plancha"
    },
    "Transats confortables autour de la piscine": {
      fr: "Transats confortables autour de la piscine",
      en: "Comfortable sun loungers around the pool",
      es: "Tumbonas cómodas alrededor de la piscina",
      it: "Lettini comodi intorno alla piscina",
      de: "Bequeme Sonnenliegen rund um den Pool",
      nl: "Comfortabele ligstoelen rond het zwembad"
    },
    "Douche extérieure": {
      fr: "Douche extérieure",
      en: "Outdoor shower",
      es: "Ducha exterior",
      it: "Doccia esterna",
      de: "Außendusche",
      nl: "Buitendouche"
    },
    // Gallery
    "Impossible de charger les photos": {
      fr: "Impossible de charger les photos",
      en: "Unable to load photos",
      es: "No se pueden cargar las fotos",
      it: "Impossibile caricare le foto",
      de: "Fotos können nicht geladen werden",
      nl: "Kan foto's niet laden"
    },
    "Location de villa de luxe au Grau du Roi": {
      fr: "Location de villa de luxe au Grau du Roi",
      en: "Luxury villa rental in Le Grau du Roi",
      es: "Alquiler de villa de lujo en Le Grau du Roi",
      it: "Affitto villa di lusso a Le Grau du Roi",
      de: "Luxus-Villavermietung in Le Grau du Roi",
      nl: "Luxe villaverhuur in Le Grau du Roi"
    },
  };

  // Check if we have a translation for this text
  if (translations[text] && translations[text][targetLang]) {
    return translations[text][targetLang];
  }
  
  // If no translation is available, return the original text
  return text;
}
